import {
  collection,
  doc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { FIREBASE_COLLECTIONS } from "../config/firebaseCollections";
import { buildDefaultScheduleCalendar } from "./signupDefaults";
import { getScheduleRuntimeMode } from "./scheduleSettings";
import { readCollectionServerPreferred } from "./firestoreCacheFirstRead";
import { normalizeSchedulesWithOrder } from "./scheduleEditorUtils";

const BATCH_LIMIT = 450;

const userRef = (uid) => doc(db, FIREBASE_COLLECTIONS.USERS, uid);

const dailySchedulesCollection = (uid) =>
  collection(db, FIREBASE_COLLECTIONS.USERS, uid, FIREBASE_COLLECTIONS.DAILY_SCHEDULES);

const scheduleAssignmentsCollection = (uid) =>
  collection(db, FIREBASE_COLLECTIONS.USERS, uid, FIREBASE_COLLECTIONS.SCHEDULE_ASSIGNMENTS);

const chunk = (items, size) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const resolveScheduleRuntimeMode = (scheduleCalendar = {}, userSettings = {}) => {
  const scheduleRuntimeMode =
    scheduleCalendar?.scheduleRuntimeMode
    ?? userSettings?.scheduleRuntimeMode
    ?? userSettings?.scheduleCalendar?.scheduleRuntimeMode;

  return getScheduleRuntimeMode({ scheduleRuntimeMode, scheduleCalendar });
};

export const sanitizeScheduleCalendar = (
  scheduleCalendar = {},
  existingScheduleCalendar = {},
  userSettings = {}
) => {
  const {
    defaultSchedule,
    scheduleRuntimeMode: _scheduleRuntimeMode,
    legacyFallbackEnabled: _legacyFallbackEnabled,
    recurringRules: _recurringRules,
    ...rest
  } = scheduleCalendar || {};

  return {
    ...(existingScheduleCalendar || {}),
    ...rest,
    scheduleRuntimeMode: resolveScheduleRuntimeMode(scheduleCalendar, userSettings),
    templates: normalizeSchedulesWithOrder(rest.templates),
    assignments: Array.isArray(rest.assignments) ? rest.assignments : [],
  };
};

const resolveTemplateId = (template, index) =>
  String(template?.id || template?.templateId || `template-${index}`);

const resolveAssignmentId = (assignment, index) =>
  String(
    assignment?.id
      || (assignment?.date ? `assignment-${assignment.date}` : "")
      || (assignment?.startDate ? `assignment-${assignment.startDate}` : "")
      || `assignment-${index}`
  );

/** @internal Exported for regression tests. */
export const getSyncDeleteDocIds = (existingSnapshot, nextItems, resolveId) => {
  const nextIds = new Set(nextItems.map((item, index) => resolveId(item, index)));
  return existingSnapshot.docs
    .filter((existingDoc) => !nextIds.has(existingDoc.id))
    .map((existingDoc) => existingDoc.id);
};

const commitCollectionUpserts = async (colRef, items, resolveId) => {
  const nextItems = Array.isArray(items) ? items : [];

  for (const indexedItems of chunk(
    nextItems.map((item, index) => ({ item, index })),
    BATCH_LIMIT
  )) {
    const batch = writeBatch(db);
    indexedItems.forEach(({ item, index }) => {
      const itemId = resolveId(item, index);
      batch.set(doc(colRef, itemId), {
        ...item,
        id: itemId,
      });
    });
    await batch.commit();
  }
};

const commitCollectionDeletes = async (colRef, deleteDocIds = []) => {
  for (const idChunk of chunk(deleteDocIds, BATCH_LIMIT)) {
    const batch = writeBatch(db);
    idChunk.forEach((docId) => {
      batch.delete(doc(colRef, docId));
    });
    await batch.commit();
  }
};

const syncCollectionDocs = async (colRef, items, resolveId) => {
  const existingSnapshot = await readCollectionServerPreferred(colRef);
  const nextItems = Array.isArray(items) ? items : [];
  const deleteDocIds = getSyncDeleteDocIds(existingSnapshot, nextItems, resolveId);

  await commitCollectionUpserts(colRef, nextItems, resolveId);
  await commitCollectionDeletes(colRef, deleteDocIds);
};

const persistScheduleRuntimeModeOnUser = async (uid, scheduleCalendar, userSettings = {}) => {
  const scheduleRuntimeMode = resolveScheduleRuntimeMode(scheduleCalendar, userSettings);
  const updatePayload = {
    "settings.scheduleRuntimeMode": scheduleRuntimeMode,
  };

  if (scheduleCalendar?.schoolYearSettings) {
    updatePayload["settings.schoolYearSettings"] = scheduleCalendar.schoolYearSettings;
  }
  if (scheduleCalendar?.scheduleManagerSetup) {
    updatePayload["settings.scheduleManagerSetup"] = scheduleCalendar.scheduleManagerSetup;
  }

  await updateDoc(userRef(uid), updatePayload);
};

export const fetchScheduleCalendar = async (uid, options = {}) => {
  if (!uid) return buildDefaultScheduleCalendar(options.userSettings);

  const userSettings = options.userSettings || {};
  const schoolYearSettings = userSettings?.schoolYearSettings;
  const scheduleManagerSetup = userSettings?.scheduleManagerSetup;

  const [templatesSnapshot, assignmentsSnapshot] = await Promise.all([
    readCollectionServerPreferred(dailySchedulesCollection(uid)),
    readCollectionServerPreferred(scheduleAssignmentsCollection(uid)),
  ]);

  const hasSubcollectionData =
    templatesSnapshot.size > 0
    || assignmentsSnapshot.size > 0;

  if (!hasSubcollectionData) {
    const defaultScheduleCalendar = buildDefaultScheduleCalendar(userSettings);
    return sanitizeScheduleCalendar(
      {
        ...defaultScheduleCalendar,
        ...(schoolYearSettings ? { schoolYearSettings } : {}),
        ...(scheduleManagerSetup ? { scheduleManagerSetup } : {}),
      },
      {},
      userSettings
    );
  }

  return sanitizeScheduleCalendar(
    {
      templates: templatesSnapshot.docs.map((templateDoc) => ({
        ...templateDoc.data(),
        id: templateDoc.data()?.id || templateDoc.id,
      })),
      assignments: assignmentsSnapshot.docs.map((assignmentDoc) => ({
        ...assignmentDoc.data(),
        id: assignmentDoc.data()?.id || assignmentDoc.id,
      })),
      ...(schoolYearSettings ? { schoolYearSettings } : {}),
      ...(scheduleManagerSetup ? { scheduleManagerSetup } : {}),
    },
    {},
    userSettings
  );
};

export const saveScheduleCalendar = async (uid, scheduleCalendar = {}, options = {}) => {
  if (!uid) {
    throw new Error("Missing user id for schedule calendar.");
  }

  const userSettings = options.userSettings || {};
  const sanitized = sanitizeScheduleCalendar(
    scheduleCalendar,
    options.existingScheduleCalendar || {},
    userSettings
  );

  await persistScheduleRuntimeModeOnUser(uid, sanitized, userSettings);

  await Promise.all([
    syncCollectionDocs(dailySchedulesCollection(uid), sanitized.templates, resolveTemplateId),
    syncCollectionDocs(
      scheduleAssignmentsCollection(uid),
      sanitized.assignments,
      resolveAssignmentId
    ),
  ]);

  return sanitized;
};

export const initializeNewUserSchedulingData = async (uid, userSettings = {}) => {
  if (!uid) return buildDefaultScheduleCalendar(userSettings);
  return saveScheduleCalendar(uid, buildDefaultScheduleCalendar(userSettings), { userSettings });
};
